/**
 * VeyroCam — Header behaviour
 *
 * Two custom elements:
 *   <veyro-header>  sticky/condense behaviour + transparent-over-hero mode
 *   <veyro-drawer>  accessible off-canvas panel (mobile nav, cart)
 *
 * Custom elements are used rather than a global init pass so that sections
 * added or re-rendered by the Theme Editor upgrade themselves automatically.
 */

(function () {
  'use strict';

  /* ======================================================================
     <veyro-header>
     ======================================================================
     Adds:
       .is-scrolled  once the page has scrolled past a threshold — swaps the
                     transparent hero treatment for the solid backdrop.
       .is-hidden    when scrolling down past the hero — reclaims vertical
                     space on mobile. Restored the moment the user scrolls up.
     ====================================================================== */

  var SCROLL_THRESHOLD = 24;   // px before the header solidifies
  var HIDE_AFTER = 320;        // px before auto-hide may engage
  var DELTA_MIN = 6;           // ignore sub-pixel / jitter scrolls

  if (!customElements.get('veyro-header')) {
    customElements.define(
      'veyro-header',
      class VeyroHeader extends HTMLElement {
        connectedCallback() {
          this.lastY = window.scrollY;
          this.ticking = false;
          this.autoHide = this.dataset.autoHide === 'true';

          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });

          this.publishHeight();
          this.resizeObserver = new ResizeObserver(
            this.publishHeight.bind(this)
          );
          this.resizeObserver.observe(this);

          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          if (this.resizeObserver) this.resizeObserver.disconnect();
        }

        /**
         * Publish the real rendered height as --vc-header-height so that
         * scroll-padding-top and any sticky offsets stay correct when the
         * announcement bar is toggled or the logo size changes in the editor.
         */
        publishHeight() {
          var height = this.offsetHeight;
          if (height > 0) {
            document.documentElement.style.setProperty(
              '--vc-header-height',
              height + 'px'
            );
          }
        }

        onScroll() {
          // Coalesce scroll events into one write per frame. Reading
          // scrollY in the handler and writing classes in rAF keeps this off
          // the layout-thrash path.
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(
            function () {
              this.update();
              this.ticking = false;
            }.bind(this)
          );
        }

        update() {
          var y = window.scrollY;
          var delta = y - this.lastY;

          this.classList.toggle('is-scrolled', y > SCROLL_THRESHOLD);

          if (this.autoHide && Math.abs(delta) > DELTA_MIN) {
            // Never hide while a menu is open — the close control lives here.
            var menuOpen = this.querySelector('[aria-expanded="true"]');
            var shouldHide = delta > 0 && y > HIDE_AFTER && !menuOpen;
            this.classList.toggle('is-hidden', shouldHide);
          }

          this.lastY = y;
        }
      }
    );
  }

  /* ======================================================================
     <veyro-drawer>
     ======================================================================
     Off-canvas panel. Handles focus trapping, scroll locking, Escape, and
     backdrop dismissal.

     Markup contract:
       <veyro-drawer id="menu-drawer">
         <div data-drawer-overlay></div>
         <div data-drawer-panel role="dialog" aria-modal="true"> … </div>
       </veyro-drawer>

     Any element with [data-drawer-toggle="menu-drawer"] opens it; any
     element with [data-drawer-close] inside closes it.
     ====================================================================== */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  if (!customElements.get('veyro-drawer')) {
    customElements.define(
      'veyro-drawer',
      class VeyroDrawer extends HTMLElement {
        connectedCallback() {
          this.panel = this.querySelector('[data-drawer-panel]');
          this.overlay = this.querySelector('[data-drawer-overlay]');
          this.isOpen = false;

          this.onKeydown = this.onKeydown.bind(this);

          if (this.overlay) {
            this.overlay.addEventListener('click', this.close.bind(this));
          }

          this.querySelectorAll('[data-drawer-close]').forEach(
            function (btn) {
              btn.addEventListener('click', this.close.bind(this));
            }.bind(this)
          );

          // Bind every toggle that points at this drawer's id.
          document
            .querySelectorAll('[data-drawer-toggle="' + this.id + '"]')
            .forEach(
              function (btn) {
                btn.addEventListener(
                  'click',
                  function (event) {
                    event.preventDefault();
                    this.toggle(btn);
                  }.bind(this)
                );
              }.bind(this)
            );
        }

        toggle(trigger) {
          this.isOpen ? this.close() : this.open(trigger);
        }

        open(trigger) {
          if (this.isOpen) return;
          this.isOpen = true;

          // Remember what opened us so focus can be handed back on close.
          this.trigger =
            trigger ||
            document.querySelector('[data-drawer-toggle="' + this.id + '"]');

          this.setAttribute('open', '');
          if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

          this.lockScroll();
          document.addEventListener('keydown', this.onKeydown);

          // Move focus into the panel on the next frame — before the
          // transition completes the panel is still visually off-screen, and
          // focusing it early causes a scroll jump in Safari.
          window.requestAnimationFrame(
            function () {
              var first = this.panel && this.panel.querySelector(FOCUSABLE);
              (first || this.panel).focus({ preventScroll: true });
            }.bind(this)
          );

          this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
        }

        close() {
          if (!this.isOpen) return;
          this.isOpen = false;

          this.removeAttribute('open');
          if (this.trigger) {
            this.trigger.setAttribute('aria-expanded', 'false');
            this.trigger.focus({ preventScroll: true });
          }

          this.unlockScroll();
          document.removeEventListener('keydown', this.onKeydown);

          this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
        }

        onKeydown(event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }

          if (event.key !== 'Tab') return;

          // Focus trap: cycle within the panel.
          var items = Array.prototype.filter.call(
            this.panel.querySelectorAll(FOCUSABLE),
            function (el) {
              return el.offsetParent !== null;   // skip hidden items
            }
          );

          if (!items.length) return;

          var first = items[0];
          var last = items[items.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        /**
         * Lock body scroll without the content shifting sideways when the
         * scrollbar disappears — compensate with equivalent padding.
         */
        lockScroll() {
          var scrollbar =
            window.innerWidth - document.documentElement.clientWidth;
          this.scrollY = window.scrollY;
          document.body.style.paddingInlineEnd = scrollbar + 'px';
          document.body.style.overflow = 'hidden';
        }

        unlockScroll() {
          document.body.style.paddingInlineEnd = '';
          document.body.style.overflow = '';
        }
      }
    );
  }

  /* ======================================================================
     Disclosure — nested submenu inside the mobile drawer.
     ====================================================================== */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-vc-disclosure]');
    if (!toggle) return;

    event.preventDefault();

    var panel = document.getElementById(
      toggle.getAttribute('aria-controls')
    );
    if (!panel) return;

    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });
})();
